import clsx from 'clsx';
import { useCallback } from 'react';
import { useToggle } from 'react-use';

import { HeartFilledIcon } from '~components/icons/HeartFilledIcon';
import { HeartIcon } from '~components/icons/HeartIcon';
import { SpinnerIcon } from '~components/icons/SpinnerIcon';
import useLabels from '~hooks/useLabels';
import { AssetId } from '~modules/media-library/model/Asset';
import { Button } from '~modules/media-library/view/Button';

type FavouriteButtonProps = {
  assetId: AssetId;
  onToggle?: (assetId: AssetId) => Promise<void> | void;
  isFavourite?: boolean;
  small?: boolean;
};

export function FavouriteButton({ assetId, onToggle, isFavourite = false, small = false }: FavouriteButtonProps) {
  const { btnFavouriteSetHint, btnFavouriteUnsetHint } = useLabels(['btnFavouriteSetHint', 'btnFavouriteUnsetHint']);
  const [isProcessing, toggleProcessing] = useToggle(false);

  const handleClick = useCallback(async () => {
    toggleProcessing();
    await onToggle?.(assetId);
    toggleProcessing();
  }, [assetId, onToggle, toggleProcessing]);

  if (!onToggle) {
    return null;
  }

  return (
    <Button
      title={isFavourite ? btnFavouriteUnsetHint : btnFavouriteSetHint}
      variant="icon"
      onClick={handleClick}
      className={clsx('ml-icon-fav', !isProcessing && isFavourite && 'text-red')}
      size={small ? 'small' : 'default'}
      disabled={isProcessing}
    >
      {isProcessing ? (
        <SpinnerIcon className="animate-spin w-4 h-4" />
      ) : (
        <>{isFavourite ? <HeartFilledIcon /> : <HeartIcon />}</>
      )}
    </Button>
  );
}
